import { useState } from 'react';

import { Alert, Button, Card, ColorSchemeSwitch } from '../shared/components';

import styles from './AuthPages.module.css';

/**
 * "Is this you?"
 *
 * Shows the name and nothing else. That is a security decision, not a layout
 * one: someone shown a name they recognise can confirm honestly, while someone
 * shown a name plus a national ID and a phone number has been handed those
 * details before proving anything (spec FR-004).
 */
export interface ConfirmIdentityPageProps {
  readonly employeeName: string;
  readonly onConfirm: () => Promise<void>;
  readonly onDecline: () => Promise<void>;
  readonly errorAr: string | null;
}

export function ConfirmIdentityPage({
  employeeName,
  onConfirm,
  onDecline,
  errorAr,
}: ConfirmIdentityPageProps) {
  const [pending, setPending] = useState<'confirm' | 'decline' | null>(null);

  const run = (which: 'confirm' | 'decline', action: () => Promise<void>) => () => {
    setPending(which);
    void action().finally(() => {
      setPending(null);
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.schemeSlot}>
        <ColorSchemeSwitch />
      </div>

      {/* The page heading lives outside the card: Card renders h2 and below by
          design, so that a panel dropped anywhere cannot claim to be the page
          title. The page still needs exactly one h1. */}
      <h1 className="visually-hidden">تأكيد الهوية</h1>

      <Card title="تأكيد الهوية">
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}

        <p className={styles.body}>وجدنا سجلًا مرتبطًا ببريدك الإلكتروني باسم:</p>

        <p className={styles.candidateName}>{employeeName}</p>

        <p className={styles.body}>هل هذا أنت؟</p>

        <div className={styles.actions}>
          <Button
            size="lg"
            fullWidth
            loading={pending === 'confirm'}
            disabled={pending === 'decline'}
            onClick={run('confirm', onConfirm)}
          >
            نعم، هذا أنا
          </Button>

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            loading={pending === 'decline'}
            disabled={pending === 'confirm'}
            onClick={run('decline', onDecline)}
          >
            لا، ليس أنا
          </Button>
        </div>

        <Alert severity="warning" title="تنبيه">
          لا تؤكّد اسمًا ليس اسمك. الربط يُسجَّل باسمك، وستظهر لك بيانات ذلك
          الموظف الصحية.
        </Alert>
      </Card>
    </div>
  );
}
